/**
 * PlanDetailDrawer — Panneau détail éditable pour un plan d'action.
 * Onglets : Détail (édition inline) / Commentaires / Pièces jointes / Historique.
 * Actions : Mettre à jour, Clôturer le plan d'action, Exporter PDF, Envoyer par mail.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import EnseigneLogo from "@/components/EnseigneLogo";
import UserAvatar from "@/components/UserAvatar";
import {
  X, Check, Sparkles, Send, Download, Lock, Plus, Trash2, Paperclip,
  MessageSquare, Upload, FileText, Image as ImageIcon, File as FileIcon,
  Search, ChevronDown,
} from "lucide-react";
import FilterMultiSelect, { SearchSelect } from "@/components/filters/FilterMultiSelect";

const KANBAN_COLS = [
  { key: "todo",        label: "À faire",     bg: "bg-slate-100 text-slate-700" },
  { key: "in_progress", label: "En cours",    bg: "bg-blue-100 text-blue-700" },
  { key: "waiting",     label: "En attente",  bg: "bg-amber-100 text-amber-700" },
  { key: "done",        label: "Terminés",    bg: "bg-emerald-100 text-emerald-700" },
];
const NATURES = ["Ressources humaines","Qualité","Organisation","Client","Commercial","Sécurité","Formation","Financier","Autre"];
export const PRIO_HEX = {
  haute:"#EF4444", critical:"#EF4444",
  elevee:"#F59E0B", high:"#F59E0B",
  moyenne:"#F59E0B", medium:"#F59E0B",
  basse:"#16A34A", low:"#16A34A",
};
export const PRIO_LBL = {
  haute:"Critique", critical:"Critique",
  elevee:"Élevée",  high:"Élevée",
  moyenne:"Moyenne", medium:"Moyenne",
  basse:"Faible",   low:"Faible",
};
export const PRIO_DEFAULT_HEX = "#9CA3AF";
const PRIORITIES = [
  { v:"haute",   l:"Critique", hex:"#EF4444" },
  { v:"elevee",  l:"Élevée",   hex:"#F59E0B" },
  { v:"moyenne", l:"Moyenne",  hex:"#F59E0B" },
  { v:"basse",   l:"Faible",   hex:"#16A34A" },
];
const ACTION_STATUSES = [
  { v:"todo",        l:"À faire"    },
  { v:"in_progress", l:"En cours"   },
  { v:"waiting",     l:"En attente" },
  { v:"done",        l:"Terminé"    },
];
const fmtDate = (d) => { if (!d) return "—"; try { return new Date(d).toLocaleDateString("fr-FR",{day:"2-digit",month:"2-digit",year:"numeric"}); } catch { return "—"; }};
const fmtDateTime = (d) => { if (!d) return "—"; try { return new Date(d).toLocaleString("fr-FR",{day:"2-digit",month:"2-digit",year:"2-digit",hour:"2-digit",minute:"2-digit"}); } catch { return "—"; }};

export function PriorityBadge({ priority, size = "sm" }) {
  const hex = PRIO_HEX[priority] || PRIO_DEFAULT_HEX;
  const lbl = PRIO_LBL[priority] || (priority ? priority : "Non définie");
  const pad = size === "xs" ? "px-1.5 py-0.5 text-[9px]" : "px-2 py-0.5 text-[10px]";
  return (
    <span className={`inline-flex items-center gap-1 rounded font-semibold ${pad}`}
          style={{ background: hex + "1F", color: hex }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: hex }}/>
      {lbl}
    </span>
  );
}

/* ─── Send Email popover ────────────────────────────────────────────── */
function SendEmailPopover({ onSend, onClose, users, saving }) {
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState([]);
  const [message, setMessage] = useState("");
  const filtered = useMemo(() => {
    const s = q.toLowerCase().trim();
    const base = users.filter(u => ["bm","manager","rt","mm","director","admin"].includes(u.role) && u.email);
    return s ? base.filter(u => (u.name||"").toLowerCase().includes(s) || (u.email||"").toLowerCase().includes(s)) : base;
  }, [users, q]);
  const toggle = (id) => setSelected(v => v.includes(id) ? v.filter(x=>x!==id) : [...v, id]);
  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/50 backdrop-blur-sm grid place-items-center p-4" onClick={onClose} data-testid="pa-send-modal">
      <div onClick={e=>e.stopPropagation()} className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[calc(100vh-2rem)] flex flex-col">
        <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-100">
          <div className="w-8 h-8 rounded bg-blue-600 text-white grid place-items-center"><Send className="w-4 h-4"/></div>
          <h3 className="font-display text-lg font-bold flex-1">Envoyer le plan par email</h3>
          <button onClick={onClose} className="w-8 h-8 rounded hover:bg-slate-100 grid place-items-center"><X className="w-4 h-4"/></button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400"/>
            <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Rechercher un destinataire..."
                   className="w-full pl-7 pr-2 py-1.5 rounded-lg border border-slate-200 text-[12px]"/>
          </div>
          <div className="max-h-64 overflow-auto rounded-lg border border-slate-200 divide-y divide-slate-100" data-testid="pa-send-list">
            {filtered.slice(0, 200).map(u => {
              const on = selected.includes(u.id);
              return (
                <button type="button" key={u.id} onClick={()=>toggle(u.id)}
                        data-testid={`pa-send-user-${u.id}`}
                        className={`w-full text-left px-3 py-2 flex items-center gap-2 text-[12px] hover:bg-slate-50 ${on?"bg-blue-50":""}`}>
                  <div className={`w-4 h-4 rounded border grid place-items-center shrink-0 ${on?"bg-blue-600 border-blue-600 text-white":"border-slate-300 bg-white"}`}>
                    {on && <Check className="w-3 h-3"/>}
                  </div>
                  <UserAvatar name={u.name} avatarUrl={u.avatar_url} size={22}/>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold truncate">{u.name}</div>
                    <div className="text-[10px] text-slate-500 truncate">{u.email}</div>
                  </div>
                </button>
              );
            })}
            {filtered.length === 0 && <div className="text-center italic text-slate-400 text-[11px] py-4">Aucun destinataire trouvé</div>}
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500 block mb-1">Message (optionnel)</label>
            <textarea value={message} onChange={e=>setMessage(e.target.value)} rows={3}
                      placeholder="Un mot d'accompagnement..." data-testid="pa-send-msg"
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px] resize-none"/>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-100 bg-slate-50">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-white">Annuler</button>
          <button onClick={()=>onSend(selected, message)} disabled={saving || selected.length===0}
                  data-testid="pa-send-submit"
                  style={{ backgroundColor: "#2563EB" }}
                  className="px-4 py-2 rounded-lg text-sm font-semibold text-white hover:brightness-110 disabled:opacity-50">
            {saving ? "Envoi…" : `Envoyer (${selected.length})`}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Close plan confirm modal ─────────────────────────────────────── */
function ClosePlanModal({ onConfirm, onClose, defaultResult = "" }) {
  const [result, setResult] = useState(defaultResult);
  const [saving, setSaving] = useState(false);
  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/50 backdrop-blur-sm grid place-items-center p-4" onClick={onClose} data-testid="pa-close-modal">
      <div onClick={e=>e.stopPropagation()} className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-100">
          <div className="w-8 h-8 rounded bg-slate-800 text-white grid place-items-center"><Lock className="w-4 h-4"/></div>
          <h3 className="font-display text-lg font-bold flex-1">Clôturer le plan d'action</h3>
          <button onClick={onClose} className="w-8 h-8 rounded hover:bg-slate-100 grid place-items-center"><X className="w-4 h-4"/></button>
        </div>
        <div className="p-4 space-y-2">
          <p className="text-[12px] text-slate-600">La clôture d'un plan est définitive. Renseignez le résultat obtenu.</p>
          <textarea value={result} onChange={e=>setResult(e.target.value)} rows={5}
                    placeholder="Quel résultat obtenu..." data-testid="pa-close-result"
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px] resize-none"/>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-100 bg-slate-50">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-white">Annuler</button>
          <button onClick={async ()=>{ if(!result.trim()){ toast.error("Résultat obligatoire"); return; } setSaving(true); try{ await onConfirm(result);}finally{ setSaving(false);} }}
                  data-testid="pa-close-confirm" disabled={saving}
                  className="px-4 py-2 rounded-lg text-sm font-semibold bg-slate-800 text-white hover:bg-slate-900 disabled:opacity-50">
            {saving ? "Clôture…" : "Clôturer"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Main Drawer ──────────────────────────────────────────────────── */
export default function PlanDetailDrawer({ plan, onClose, onSaved, users = [], stores = [] }) {
  const { user } = useAuth() || {};
  const [tab, setTab] = useState("detail");
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [postingComment, setPostingComment] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [showSend, setShowSend] = useState(false);
  const [showClose, setShowClose] = useState(false);
  const fileInput = useRef(null);

  // Initialize form when plan changes
  useEffect(() => {
    if (!plan) return;
    setF({
      title: plan.title || "",
      constat: plan.constat || plan.description || "",
      objective: plan.objective || "",
      priority: plan.priority || "moyenne",
      natures: plan.natures || (plan.category ? [plan.category] : []),
      nature_other: plan.nature_other || "",
      deadline: plan.deadline || "",
      status: plan.status || "todo",
      actions: (plan.actions || []).map(a => ({...a})),
      result: plan.result || "",
    });
    setAttachments(plan.attachments || []);
    setTab("detail");
  }, [plan?.id]);

  // Load comments when opening
  useEffect(() => {
    if (!plan?.id) return;
    api.get(`/ops/plans/${plan.id}/comments`).then(r => setComments(r.data || [])).catch(()=>{});
  }, [plan?.id]);

  const userOptions = useMemo(
    () => users.filter(u => ["bm","manager","rt","mm","director","admin"].includes(u.role)),
    [users]
  );

  if (!plan || !f) return null;

  const toggleNature = (n) => setF(v => ({...v, natures: v.natures.includes(n) ? v.natures.filter(x=>x!==n) : [...v.natures, n]}));
  const addAction = () => setF(v => ({...v, actions: [...v.actions, { description:"", responsible_id: user?.id || "", deadline:"", status:"todo" }]}));
  const updAction = (i, patch) => setF(v => ({...v, actions: v.actions.map((a,idx)=> idx===i ? {...a, ...patch} : a)}));
  const rmAction = (i) => setF(v => ({...v, actions: v.actions.filter((_,idx)=>idx!==i)}));

  const buildPayload = () => {
    const actions = f.actions.filter(a => (a.description||"").trim()).map(a => {
      const u = users.find(u => u.id === a.responsible_id);
      return { ...a, responsible: u?.name };
    });
    return {
      title: f.title, constat: f.constat, description: f.constat, objective: f.objective,
      priority: f.priority, natures: f.natures, nature_other: f.nature_other,
      deadline: f.deadline || null, status: f.status,
      actions, result: f.result,
    };
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/ops/plans/${plan.id}`, buildPayload());
      toast.success("Plan mis à jour");
      onSaved?.(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const closePlan = async (result) => {
    try {
      const { data } = await api.post(`/ops/plans/${plan.id}/close`, { result });
      toast.success("Plan clôturé");
      setShowClose(false);
      onSaved?.(data);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); throw e; }
  };

  const postComment = async () => {
    if (!newComment.trim()) return;
    setPostingComment(true);
    try {
      const { data } = await api.post(`/ops/plans/${plan.id}/comments`, { text: newComment });
      setComments(v => [...v, data]);
      setNewComment("");
      toast.success("Commentaire ajouté");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setPostingComment(false); }
  };

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    setUploadingFile(true);
    try {
      for (const file of files) {
        const fd = new FormData(); fd.append("file", file);
        const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" }});
        const url = `${api.defaults.baseURL}/files/${up.data.path}`;
        const { data } = await api.post(`/ops/plans/${plan.id}/attachments`, {
          url, name: file.name, mime: file.type, size: file.size });
        setAttachments(v => [...v, data]);
      }
      toast.success(`${files.length} fichier${files.length>1?"s":""} ajouté${files.length>1?"s":""}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur upload"); }
    finally { setUploadingFile(false); }
  };

  const removeAttachment = async (aid) => {
    try {
      await api.delete(`/ops/plans/${plan.id}/attachments/${aid}`);
      setAttachments(v => v.filter(a => a.id !== aid));
      toast.success("Pièce jointe supprimée");
    } catch (e) { toast.error("Erreur"); }
  };

  const exportPdf = async () => {
    try {
      const r = await api.get(`/ops/plans/${plan.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `plan_action_${plan.id}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF téléchargé");
    } catch (e) { toast.error("Erreur export"); }
  };

  const sendEmail = async (recipient_ids, message) => {
    if (recipient_ids.length === 0) { toast.error("Sélectionnez au moins un destinataire"); return; }
    try {
      const { data } = await api.post(`/ops/plans/${plan.id}/send-email`, { recipient_ids, message });
      if (data.count > 0) {
        toast.success(`Envoyé à ${data.count} destinataire${data.count>1?"s":""}`);
      } else if ((data.total || 0) > 0) {
        toast.warning("Email non configuré (mode démo) — aucun email n'a été envoyé");
      } else {
        toast.error("Aucun destinataire valide");
      }
      setShowSend(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur envoi"); }
  };

  const statusInfo = KANBAN_COLS.find(c => c.key === f.status);
  const isDone = f.status === "done";

  return (
    <div className="fixed inset-0 z-40 flex justify-end pointer-events-none">
      <div className="fixed inset-0 bg-black/20 pointer-events-auto" onClick={onClose}/>
      <div className="relative w-full max-w-xl h-full bg-white shadow-2xl border-l border-slate-200 pointer-events-auto flex flex-col" data-testid="pa-detail">
        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
          <EnseigneLogo enseigne={plan.enseigne} size={30}/>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-bold text-slate-900 truncate">{f.title || plan.title}</div>
            <div className="text-[10px] text-slate-500 truncate">{(plan.corners||[]).join(", ") || plan.corner}</div>
          </div>
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${statusInfo?.bg || "bg-slate-100 text-slate-700"}`}>{statusInfo?.label || f.status}</span>
          <button onClick={onClose} className="w-7 h-7 grid place-items-center rounded hover:bg-slate-100" data-testid="pa-detail-close"><X className="w-4 h-4"/></button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 px-3 border-b border-slate-100">
          {[["detail","Détail"],["comments",`Commentaires (${comments.length})`],["attachments",`Pièces jointes (${attachments.length})`],["history","Historique"]].map(([k,l])=>(
            <button key={k} onClick={()=>setTab(k)} data-testid={`pa-dtab-${k}`}
                    className={`px-3 py-2 text-[11px] font-semibold border-b-2 transition whitespace-nowrap ${tab===k?"border-blue-600 text-blue-600":"border-transparent text-slate-500 hover:text-slate-700"}`}>{l}</button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-4 space-y-4">
          {tab === "detail" && (
            <>
              {/* Priorité + Statut */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] uppercase font-semibold text-slate-500 mb-1">Priorité</div>
                  <div className="grid grid-cols-2 gap-1.5" data-testid="pa-edit-prio">
                    {PRIORITIES.map(p => {
                      const on = f.priority === p.v;
                      return (
                        <button key={p.v} type="button" onClick={()=>setF(v=>({...v, priority:p.v}))}
                                style={on ? { borderColor: p.hex, background: p.hex+"18" } : {}}
                                data-testid={`pa-edit-prio-${p.v}`}
                                className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg border-2 text-[11px] font-semibold transition ${on?"":"border-slate-200 hover:border-slate-300"}`}>
                          <div className="w-2 h-2 rounded-full" style={{ background: p.hex }}/>
                          <span style={on ? { color: p.hex } : { color:"#334155" }}>{p.l}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase font-semibold text-slate-500 mb-1">Statut</div>
                  <SearchSelect value={f.status} onChange={val => setF(v=>({...v, status:val}))} options={[...KANBAN_COLS.map(c => ({ value: c.key, label: c.label }))]} data-testid="pa-edit-status" size="lg" menuWidth={260}/>
                </div>
              </div>

              {/* Natures */}
              <div>
                <div className="text-[10px] uppercase font-semibold text-slate-500 mb-1">Nature de la problématique</div>
                <div className="flex flex-wrap gap-1.5" data-testid="pa-edit-natures">
                  {NATURES.map(n => {
                    const on = f.natures.includes(n);
                    return (
                      <button key={n} type="button" onClick={()=>toggleNature(n)}
                              className={`text-[10px] font-semibold px-2 py-1 rounded-full border transition ${on?"bg-blue-50 border-blue-500 text-blue-700":"bg-white border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                        {n}
                      </button>
                    );
                  })}
                </div>
                {f.natures.includes("Autre") && (
                  <input value={f.nature_other} onChange={e=>setF(v=>({...v, nature_other:e.target.value}))}
                         placeholder="Précisez..." maxLength={80}
                         className="mt-2 w-full px-3 py-1.5 rounded-lg border border-slate-200 text-[12px]"/>
                )}
              </div>

              {/* Constat */}
              <div>
                <div className="text-[10px] uppercase font-semibold text-slate-500 mb-1">Constat</div>
                <textarea rows={3} value={f.constat} onChange={e=>setF(v=>({...v, constat:e.target.value}))}
                          data-testid="pa-edit-constat"
                          className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[12px] resize-none"/>
              </div>

              {/* Objectif */}
              <div>
                <div className="text-[10px] uppercase font-semibold text-slate-500 mb-1">Objectif</div>
                <input value={f.objective} onChange={e=>setF(v=>({...v, objective:e.target.value}))}
                       data-testid="pa-edit-objective"
                       className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[12px]"/>
              </div>

              {/* Date fin */}
              <div>
                <div className="text-[10px] uppercase font-semibold text-slate-500 mb-1">Date de fin prévue</div>
                <input type="date" value={f.deadline || ""} onChange={e=>setF(v=>({...v, deadline:e.target.value}))}
                       data-testid="pa-edit-deadline"
                       className="w-full sm:w-52 px-3 py-2 rounded-lg border border-slate-200 text-[12px]"/>
              </div>

              {/* Actions */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-[10px] uppercase font-semibold text-slate-500">Actions</div>
                  <button type="button" onClick={addAction} data-testid="pa-edit-add-action"
                          className="flex items-center gap-1 text-[11px] font-semibold text-blue-600 hover:text-blue-700 px-2 py-0.5 rounded hover:bg-blue-50">
                    <Plus className="w-3.5 h-3.5"/> Ajouter
                  </button>
                </div>
                {f.actions.length === 0 ? (
                  <div className="text-center py-3 text-[11px] italic text-slate-400 border border-dashed border-slate-200 rounded-lg">
                    Aucune action
                  </div>
                ) : (
                  <div className="space-y-1.5" data-testid="pa-edit-actions-list">
                    {f.actions.map((a, i) => (
                      <div key={i} className="grid grid-cols-12 gap-1.5 items-center bg-slate-50 rounded-lg p-2">
                        <input value={a.description||""} onChange={e=>updAction(i,{description:e.target.value})}
                               placeholder="Description" data-testid={`pa-edit-action-desc-${i}`}
                               className="col-span-12 sm:col-span-5 px-2 py-1 rounded border border-slate-200 text-[11px] bg-white"/>
                        <SearchSelect value={a.responsible_id||""} onChange={v => updAction(i,{responsible_id:v})} options={[...userOptions.map(u => ({ value: u.id, label: u.name }))]} placeholder={"— Resp. —"} size="lg" menuWidth={260}/>
                        <input type="date" value={a.deadline||""} onChange={e=>updAction(i,{deadline:e.target.value})}
                               className="col-span-4 sm:col-span-2 px-1 py-1 rounded border border-slate-200 text-[10px] bg-white"/>
                        <SearchSelect value={a.status||"todo"} onChange={v => updAction(i,{status:v})} options={[...ACTION_STATUSES.map(s => ({ value: s.v, label: s.l }))]} size="lg" menuWidth={260}/>
                        <button type="button" onClick={()=>rmAction(i)}
                                className="col-span-2 sm:col-span-1 w-7 h-7 mx-auto rounded hover:bg-rose-100 text-slate-400 hover:text-rose-600 grid place-items-center">
                          <Trash2 className="w-3.5 h-3.5"/>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Résultat (visible si en cours de clôture ou déjà clos) */}
              <div>
                <div className="text-[10px] uppercase font-semibold text-slate-500 mb-1">Résultat</div>
                <textarea rows={3} value={f.result} onChange={e=>setF(v=>({...v, result:e.target.value}))}
                          placeholder="Quel résultat obtenu..." data-testid="pa-edit-result"
                          className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[12px] resize-none"/>
              </div>

              {/* Action buttons */}
              <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-100">
                <button onClick={exportPdf} data-testid="pa-export-pdf"
                        className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-semibold border border-slate-200 hover:bg-slate-50">
                  <Download className="w-3.5 h-3.5"/> Exporter PDF
                </button>
                <button onClick={()=>setShowSend(true)} data-testid="pa-send-email"
                        className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-semibold border border-slate-200 hover:bg-slate-50">
                  <Send className="w-3.5 h-3.5"/> Envoyer par mail
                </button>
                <button onClick={()=>setShowClose(true)} disabled={isDone} data-testid="pa-close-plan"
                        className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-semibold bg-slate-800 text-white hover:bg-slate-900 disabled:opacity-40">
                  <Lock className="w-3.5 h-3.5"/> {isDone ? "Plan clôturé" : "Clôturer le plan d'action"}
                </button>
                <button onClick={save} disabled={saving} data-testid="pa-update"
                        style={{ backgroundColor: "#2563EB" }}
                        className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-semibold text-white hover:brightness-110 disabled:opacity-50">
                  <Check className="w-3.5 h-3.5"/> {saving ? "Enregistrement…" : "Mettre à jour"}
                </button>
              </div>
            </>
          )}

          {tab === "comments" && (
            <div className="space-y-3" data-testid="pa-comments">
              <div className="space-y-2 max-h-72 overflow-auto">
                {comments.length === 0 && <div className="italic text-slate-400 text-[11px] text-center py-4">Aucun commentaire</div>}
                {comments.map(c => (
                  <div key={c.id} className="flex items-start gap-2" data-testid={`pa-comment-${c.id}`}>
                    <UserAvatar name={c.user_name} avatarUrl={c.user_avatar} size={28}/>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[12px] font-bold text-slate-800">{c.user_name}</span>
                        <span className="text-[10px] text-slate-400">{fmtDateTime(c.created_at)}</span>
                      </div>
                      <div className="text-[12px] text-slate-700 mt-0.5 whitespace-pre-line">{c.text}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="border-t border-slate-100 pt-3">
                <div className="flex items-start gap-2">
                  <UserAvatar name={user?.name} avatarUrl={user?.avatar_url} size={28}/>
                  <div className="flex-1">
                    <textarea value={newComment} onChange={e=>setNewComment(e.target.value)} rows={2}
                              placeholder="Ajouter un commentaire..." data-testid="pa-comment-input"
                              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[12px] resize-none"/>
                    <div className="flex justify-end mt-1.5">
                      <button onClick={postComment} disabled={postingComment || !newComment.trim()}
                              data-testid="pa-comment-submit"
                              style={{ backgroundColor: "#2563EB" }}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold text-white hover:brightness-110 disabled:opacity-50">
                        <MessageSquare className="w-3.5 h-3.5"/> {postingComment ? "Publication…" : "Publier"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {tab === "attachments" && (
            <div className="space-y-3" data-testid="pa-attachments">
              <input ref={fileInput} type="file" multiple className="hidden"
                     accept="image/*,application/pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                     onChange={(e)=>{ uploadFiles(e.target.files); e.target.value=""; }}
                     data-testid="pa-file-input"/>
              <div
                onDragOver={(e)=>{ e.preventDefault(); setDragOver(true); }}
                onDragLeave={()=>setDragOver(false)}
                onDrop={(e)=>{ e.preventDefault(); setDragOver(false); uploadFiles(e.dataTransfer.files); }}
                onClick={()=>fileInput.current?.click()}
                data-testid="pa-file-dropzone"
                className={`cursor-pointer rounded-lg border-2 border-dashed transition p-4 flex items-center gap-3 ${dragOver?"border-blue-400 bg-blue-50":"border-slate-200 hover:border-blue-300 hover:bg-slate-50"}`}>
                <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-600 grid place-items-center shrink-0"><Upload className="w-5 h-5"/></div>
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] font-semibold text-slate-800">{uploadingFile ? "Envoi en cours…" : "Cliquer ou glisser des fichiers ici"}</div>
                  <div className="text-[10px] text-slate-500">Images, PDF, Word — plusieurs fichiers acceptés</div>
                </div>
              </div>

              <div className="space-y-1.5">
                {attachments.length === 0 && <div className="italic text-slate-400 text-[11px] text-center py-4">Aucune pièce jointe</div>}
                {attachments.map(a => {
                  const isImg = (a.mime||"").startsWith("image/");
                  const isPdf = (a.mime||"") === "application/pdf" || (a.name||"").toLowerCase().endsWith(".pdf");
                  const Icon = isImg ? ImageIcon : isPdf ? FileText : FileIcon;
                  return (
                    <div key={a.id} className="flex items-center gap-2 rounded-lg border border-slate-200 p-2 bg-white" data-testid={`pa-attach-${a.id}`}>
                      <div className="w-9 h-9 rounded bg-slate-100 grid place-items-center shrink-0 overflow-hidden">
                        {isImg ? <img src={a.url} alt="" className="w-full h-full object-cover"/> : <Icon className="w-4 h-4 text-slate-500"/>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[12px] font-semibold text-slate-800 truncate">{a.name}</div>
                        <div className="text-[10px] text-slate-500">{fmtDateTime(a.uploaded_at)} · {a.uploaded_by_name || "—"} · {a.size ? Math.round(a.size/1024)+" Ko" : ""}</div>
                      </div>
                      <a href={a.url} target="_blank" rel="noreferrer" className="w-7 h-7 rounded hover:bg-slate-100 grid place-items-center text-slate-500" title="Ouvrir"><Download className="w-3.5 h-3.5"/></a>
                      <button onClick={()=>removeAttachment(a.id)} data-testid={`pa-attach-rm-${a.id}`}
                              className="w-7 h-7 rounded hover:bg-rose-100 grid place-items-center text-slate-400 hover:text-rose-600" title="Supprimer">
                        <Trash2 className="w-3.5 h-3.5"/>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {tab === "history" && (
            <div className="space-y-2 text-[11px] text-slate-600" data-testid="pa-history">
              <div>• Créé le {fmtDateTime(plan.created_at)} par {plan.responsible || "—"}</div>
              <div>• Dernière mise à jour : {fmtDateTime(plan.updated_at)}</div>
              <div>• Statut actuel : {statusInfo?.label || f.status}</div>
              {plan.closed_at && <div>• Clôturé le {fmtDateTime(plan.closed_at)}{plan.closed_by_name ? ` par ${plan.closed_by_name}`:""}</div>}
              {plan.result && <div className="mt-2 p-2 bg-emerald-50 border border-emerald-100 rounded"><b>Résultat :</b><br/>{plan.result}</div>}
            </div>
          )}
        </div>
      </div>

      {showSend && <SendEmailPopover onSend={sendEmail} onClose={()=>setShowSend(false)} users={users} saving={saving}/>}
      {showClose && <ClosePlanModal onConfirm={closePlan} onClose={()=>setShowClose(false)} defaultResult={f.result}/>}
    </div>
  );
}
